import * as THREE from "three";
import { clamp, damp } from "../core/math.js";

const vertexShader = `
  attribute float aSize;
  attribute float aPhase;
  uniform float uTime;
  uniform float uPixelRatio;
  uniform vec2 uPointer;
  uniform vec2 uAnimal;
  uniform float uAnimalStrength;
  uniform float uScroll;
  varying float vAlpha;

  void main() {
    vec3 p = position;
    float drift = sin(uTime * 0.34 + aPhase) * 0.025;
    p.x += drift + uScroll * (0.008 + p.z * 0.004);
    p.y += cos(uTime * 0.28 + aPhase * 1.7) * 0.016;
    p.xy += uPointer * (0.01 + (p.z + 1.0) * 0.008);

    vec2 toAnimal = uAnimal - p.xy;
    float animalDistance = max(length(toAnimal), 0.04);
    p.xy += normalize(toAnimal) * uAnimalStrength * 0.045 / (1.0 + animalDistance * 8.0);

    vec4 viewPosition = modelViewMatrix * vec4(p, 1.0);
    gl_Position = projectionMatrix * viewPosition;
    gl_PointSize = aSize * uPixelRatio * (1.0 + uAnimalStrength * 0.28);
    vAlpha = 0.36 + 0.18 * sin(uTime * 0.5 + aPhase);
  }
`;

const fragmentShader = `
  uniform vec3 uGold;
  uniform vec3 uBeam;
  uniform float uAnimalStrength;
  varying float vAlpha;

  void main() {
    vec2 centered = gl_PointCoord - vec2(0.5);
    float distanceToCenter = length(centered);
    float alpha = smoothstep(0.5, 0.08, distanceToCenter) * vAlpha;
    vec3 color = mix(uGold, uBeam, 0.28 + uAnimalStrength * 0.35);
    gl_FragColor = vec4(color, alpha);
  }
`;

export class ForestScene {
  #canvas;
  #store;
  #config;
  #tier;
  #renderer = null;
  #scene = null;
  #camera = null;
  #particles = null;
  #particleMaterial = null;
  #leaves = null;
  #pointer = { x: 0, y: 0 };
  #pointerCurrent = { x: 0, y: 0 };
  #animal = { x: 0, y: 0, strength: 0 };
  #animalCurrent = { x: 0, y: 0, strength: 0 };
  #wind = 0;
  #elapsed = 0;
  #resizeHandler;
  #offDowngrade;

  constructor({ canvas, store, config, tier }) {
    this.#canvas = canvas;
    this.#store = store;
    this.#config = config;
    this.#tier = tier;
  }

  init() {
    if (this.#tier === "low" || this.#tier === "reduced") {
      this.#canvas.hidden = true;
      return false;
    }

    try {
      this.#renderer = new THREE.WebGLRenderer({
        canvas: this.#canvas,
        alpha: true,
        antialias: false,
        powerPreference:
          this.#tier === "high" ? "high-performance" : "low-power"
      });
      this.#renderer.setClearColor(0x000000, 0);
      this.#renderer.outputColorSpace = THREE.SRGBColorSpace;
      this.#scene = new THREE.Scene();
      this.#camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 10);
      this.#camera.position.z = 4;

      this.#createParticles();
      this.#createLeaves();
      this.#resize();
      this.#resizeHandler = () => this.#resize();
      window.addEventListener("resize", this.#resizeHandler);
      this.#canvas.addEventListener(
        "webglcontextlost",
        (event) => {
          event.preventDefault();
          this.disable();
          this.#store.patch(
            {
              performance: {
                webglAvailable: false,
                tier: "low"
              }
            },
            "performance:webgl-lost"
          );
        },
        { once: true }
      );
      this.#offDowngrade = this.#store.on(
        "performance:downgrade",
        (event) => this.setTier(event.detail.performance.tier)
      );
      document.body.classList.add("scene-ready");
      return true;
    } catch (error) {
      this.#store.emit("scene:error", { error });
      this.disable();
      return false;
    }
  }

  #createParticles() {
    const count = this.#config.particles[this.#tier];
    const positions = new Float32Array(count * 3);
    const sizes = new Float32Array(count);
    const phases = new Float32Array(count);

    for (let index = 0; index < count; index += 1) {
      const offset = index * 3;
      positions[offset] = Math.random() * 2.2 - 1.1;
      positions[offset + 1] = Math.random() * 2 - 1;
      positions[offset + 2] = Math.random() * 1.5 - 0.75;
      sizes[index] = 1.5 + Math.random() * 3.2;
      phases[index] = Math.random() * Math.PI * 2;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute(
      "position",
      new THREE.BufferAttribute(positions, 3)
    );
    geometry.setAttribute("aSize", new THREE.BufferAttribute(sizes, 1));
    geometry.setAttribute("aPhase", new THREE.BufferAttribute(phases, 1));

    this.#particleMaterial = new THREE.ShaderMaterial({
      vertexShader,
      fragmentShader,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      uniforms: {
        uTime: { value: 0 },
        uPixelRatio: { value: 1 },
        uPointer: { value: new THREE.Vector2() },
        uAnimal: { value: new THREE.Vector2() },
        uAnimalStrength: { value: 0 },
        uScroll: { value: 0 },
        uGold: { value: new THREE.Color("#e8d9a0") },
        uBeam: { value: new THREE.Color("#f7f0d2") }
      }
    });

    this.#particles = new THREE.Points(geometry, this.#particleMaterial);
    this.#particles.frustumCulled = false;
    this.#scene.add(this.#particles);
  }

  #createLeaves() {
    if (this.#tier !== "high") {
      return;
    }
    const geometry = new THREE.PlaneGeometry(0.24, 0.07);
    const material = new THREE.MeshBasicMaterial({
      color: 0x172b18,
      transparent: true,
      opacity: 0.16,
      side: THREE.DoubleSide,
      depthWrite: false
    });
    this.#leaves = new THREE.InstancedMesh(geometry, material, 6);
    const dummy = new THREE.Object3D();
    const positions = [
      [-0.96, 0.72, 0.1, 0.5],
      [-0.84, -0.58, 0.15, -0.3],
      [0.9, 0.44, 0.08, -0.8],
      [0.82, -0.64, 0.12, 0.6],
      [-0.7, 0.9, 0.04, 0.15],
      [0.98, -0.05, 0.1, 1.1]
    ];
    positions.forEach(([x, y, z, rotation], index) => {
      dummy.position.set(x, y, z);
      dummy.rotation.z = rotation;
      dummy.scale.setScalar(0.7 + (index % 3) * 0.18);
      dummy.updateMatrix();
      this.#leaves.setMatrixAt(index, dummy.matrix);
    });
    this.#scene.add(this.#leaves);
  }

  #resize() {
    if (!this.#renderer || !this.#camera) {
      return;
    }
    const pixelRatio = Math.min(
      window.devicePixelRatio,
      this.#config.pixelRatio[this.#tier] ?? 1
    );
    this.#renderer.setPixelRatio(pixelRatio);
    this.#renderer.setSize(window.innerWidth, window.innerHeight, false);
    if (this.#particleMaterial) {
      this.#particleMaterial.uniforms.uPixelRatio.value = pixelRatio;
    }
  }

  setPointer(normalizedX, normalizedY) {
    this.#pointer.x = clamp(-1, normalizedX, 1);
    this.#pointer.y = clamp(-1, normalizedY, 1);
  }

  setAnimalTarget(target) {
    if (!target) {
      this.#animal.strength = 0;
      return;
    }
    this.#animal.x = (target.x / window.innerWidth) * 2 - 1;
    this.#animal.y = -((target.y / window.innerHeight) * 2 - 1);
    this.#animal.strength = clamp(0, target.strength, 1);
  }

  burstWind(amount = 1) {
    this.#wind = clamp(-1.5, this.#wind + amount, 1.5);
  }

  setTier(tier) {
    if (tier === this.#tier) {
      return;
    }
    this.#tier = tier;
    if (tier === "low" || tier === "reduced") {
      this.disable();
      return;
    }
    if (this.#particles) {
      const count = this.#config.particles[tier];
      this.#particles.geometry.setDrawRange(0, count);
    }
    if (this.#leaves) {
      this.#leaves.visible = tier === "high";
    }
    this.#resize();
  }

  update(deltaSeconds, scrollVelocity = 0) {
    if (!this.#renderer || !this.#scene || document.hidden) {
      return;
    }
    this.#elapsed += deltaSeconds;
    this.#pointerCurrent.x = damp(
      this.#pointerCurrent.x,
      this.#pointer.x,
      3.2,
      deltaSeconds
    );
    this.#pointerCurrent.y = damp(
      this.#pointerCurrent.y,
      this.#pointer.y,
      3.2,
      deltaSeconds
    );
    this.#animalCurrent.x = damp(
      this.#animalCurrent.x,
      this.#animal.x,
      4,
      deltaSeconds
    );
    this.#animalCurrent.y = damp(
      this.#animalCurrent.y,
      this.#animal.y,
      4,
      deltaSeconds
    );
    this.#animalCurrent.strength = damp(
      this.#animalCurrent.strength,
      this.#animal.strength,
      4,
      deltaSeconds
    );
    this.#wind = damp(this.#wind, 0, 1.7, deltaSeconds);

    const uniforms = this.#particleMaterial.uniforms;
    uniforms.uTime.value = this.#elapsed;
    uniforms.uPointer.value.set(
      this.#pointerCurrent.x,
      this.#pointerCurrent.y
    );
    uniforms.uAnimal.value.set(
      this.#animalCurrent.x,
      this.#animalCurrent.y
    );
    uniforms.uAnimalStrength.value = this.#animalCurrent.strength;
    uniforms.uScroll.value =
      clamp(-1, scrollVelocity / 1400, 1) + this.#wind;

    if (this.#leaves) {
      this.#leaves.position.x = this.#pointerCurrent.x * -0.04;
      this.#leaves.position.y = this.#pointerCurrent.y * -0.025;
      this.#leaves.rotation.z = this.#wind * 0.018;
    }
    this.#renderer.render(this.#scene, this.#camera);
  }

  disable() {
    this.#canvas.hidden = true;
    document.body.classList.remove("scene-ready");
    if (this.#renderer) {
      this.#renderer.dispose();
      this.#renderer = null;
    }
  }

  getStats() {
    return {
      drawCalls: this.#renderer?.info.render.calls ?? 0,
      particles:
        this.#particles?.geometry.drawRange.count ??
        this.#config.particles[this.#tier] ??
        0
    };
  }

  destroy() {
    window.removeEventListener("resize", this.#resizeHandler);
    this.#offDowngrade?.();
    this.#particles?.geometry.dispose();
    this.#particleMaterial?.dispose();
    this.#leaves?.geometry.dispose();
    this.#leaves?.material.dispose();
    this.#renderer?.dispose();
    document.body.classList.remove("scene-ready");
  }
}
